import React from 'react';
import { usePaymentInstallment } from './hooks/usePaymentInstallment';
import { ActionBar, DeleteDialog, FormDialog } from './components';
import { ErpDataTableV2 } from '@/components/custom/erp';
import { getDataTableColumns } from './cols-definition';
import type { DotThanhToan } from '@/types/schemas';
import type { FormValues } from './schema';
import { useForms } from '@/hooks';

export default function DotThanhToan() {
  const {
    paymentInstallments,
    isLoading,
    addPaymentInstallment,
    updatePaymentInstallment,
    deletePaymentInstallment,
    refreshPaymentInstallments
  } = usePaymentInstallment();

  const {
    showForm,
    showDelete,
    formMode,
    selectedObj,
    selectedRowIndex,
    isCopyMode,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection
  } = useForms<DotThanhToan>();

  const handleFormSubmit = async (data: FormValues) => {
    try {
      if (formMode === 'add') {
        await addPaymentInstallment({
          ma_dtt: data.ma_dtt,
          ten_dtt: data.ten_dtt,
          ten_dtt2: data.ten_dtt2,
          stt: Number(data.stt),
          status: data.status
        });
      } else if (formMode === 'edit' && selectedObj) {
        await updatePaymentInstallment({
          uuid: selectedObj.uuid,
          ma_dtt: data.ma_dtt,
          ten_dtt: data.ten_dtt,
          ten_dtt2: data.ten_dtt2,
          stt: Number(data.stt),
          status: data.status
        });
      }
      handleCloseForm();
      clearSelection();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  const handleDeleteConfirm = async () => {
    if (selectedObj) {
      try {
        await deletePaymentInstallment(selectedObj.uuid);
        handleCloseDelete();
        clearSelection();
      } catch (error) {
        console.error('Error deleting payment installment:', error);
      }
    }
  };

  const tables = [
    {
      name: '',
      rows: paymentInstallments,
      columns: getDataTableColumns()
    }
  ];

  return (
    <div className='flex h-screen flex-col lg:overflow-hidden'>
      {showDelete && <DeleteDialog open={showDelete} onClose={handleCloseDelete} onConfirm={handleDeleteConfirm} />}
      {showForm && (
        <FormDialog
          open={showForm}
          onAdd={handleAddClick}
          onEdit={handleEditClick}
          onDelete={handleDeleteClick}
          onCopy={handleCopyClick}
          onClose={handleCloseForm}
          onSubmit={handleFormSubmit}
          formMode={formMode}
          initialData={
            selectedObj && (formMode === 'edit' || formMode === 'view' || (formMode === 'add' && isCopyMode))
              ? {
                  ma_dtt: formMode === 'add' && isCopyMode ? `${selectedObj.ma_dtt}_copy` : selectedObj.ma_dtt,
                  ten_dtt: selectedObj.ten_dtt,
                  ten_dtt2: selectedObj.ten_dtt2 === null ? '' : selectedObj.ten_dtt2,
                  stt: selectedObj.stt,
                  status: selectedObj.status
                }
              : formMode === 'add' && !isCopyMode
                ? undefined
                : undefined
          }
          onAddButtonClick={() => {
            handleCloseForm();
            clearSelection();
            setTimeout(() => {
              handleAddClick();
            }, 100);
          }}
          onEditButtonClick={() => {
            if (selectedObj) {
              handleCloseForm();
              handleEditClick();
            }
          }}
          onDeleteButtonClick={() => {
            if (selectedObj) {
              handleCloseForm();
              handleDeleteClick();
            }
          }}
          onCopyButtonClick={() => {
            if (selectedObj) {
              handleCloseForm();
              handleCopyClick();
            }
          }}
        />
      )}

      <div className='w-full'>
        <ActionBar
          onAddClick={handleAddClick}
          onEditClick={() => selectedObj && handleEditClick()}
          onDeleteClick={() => selectedObj && handleDeleteClick()}
          onCopyClick={() => selectedObj && handleCopyClick()}
          onViewClick={() => selectedObj && handleViewClick()}
          onRefreshClick={() => refreshPaymentInstallments()}
        />

        <ErpDataTableV2
          tables={tables}
          selectedRowId={selectedRowIndex || undefined}
          onRowClick={handleRowClick}
          loading={isLoading}
        />
      </div>
    </div>
  );
}
